// Global-search SWR hooks. The topbar `<SearchBar>` uses `useSearch`
// for its debounced popover; the `/search` page (item 13) uses
// `usePagedSearch` for the full results table with format/repo/kind
// filters and cursor pagination.
//
// Backend lives at /api/v1/search?q=… and returns a flat array of
// hits with `kind: "artifact" | "repo"`. The artifact-side cursor
// is surfaced via the `X-Next-Cursor` response header so the
// popover's array contract stays intact.

import { useEffect, useState } from 'react';
import useSWR from 'swr';

import { parseErrorResponse } from './problem';

export type SearchKind = 'artifact' | 'repo';

export interface SearchHit {
  kind: SearchKind;
  id: string;
  title: string;
  subtitle: string;
  format?: string;
  repoId?: string;
  repoSlug?: string;
  repoKind?: string;
  projectSlug?: string;
  version?: string;
  path?: string;
  sha256?: string;
  size?: number;
  uploadedAt?: string;
}

async function fetchSearch(url: string): Promise<SearchHit[]> {
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw await parseErrorResponse(res);
  return res.json();
}

export function useSearch(query: string, limit = 20) {
  const trimmed = query.trim();
  const key = trimmed
    ? `/api/v1/search?q=${encodeURIComponent(trimmed)}&limit=${limit}`
    : null;
  const { data, error, isLoading } = useSWR<SearchHit[]>(key, fetchSearch, {
    keepPreviousData: true,
    revalidateOnFocus: false,
    dedupingInterval: 200,
  });
  return {
    hits: data ?? [],
    isLoading: !!key && isLoading,
    error,
  };
}

// Filter options accepted by the dedicated /search page.
export interface PagedSearchFilters {
  format?: string;
  repo?: string;
  kind?: '' | 'local' | 'remote' | 'virtual';
}

export interface PagedSearchPage {
  hits: SearchHit[];
  nextCursor: string;
  isLoading: boolean;
  error?: Error;
}

// Build the query string for /api/v1/search with filter + cursor.
function buildSearchURL(
  q: string,
  filters: PagedSearchFilters,
  cursor: string,
  limit: number,
): string {
  const params = new URLSearchParams();
  params.set('q', q);
  params.set('limit', String(limit));
  if (filters.format) params.set('format', filters.format);
  if (filters.repo) params.set('repo', filters.repo);
  if (filters.kind) params.set('kind', filters.kind);
  if (cursor) params.set('cursor', cursor);
  return `/api/v1/search?${params.toString()}`;
}

// Fetch wrapper that also surfaces the X-Next-Cursor header.
async function fetchSearchWithCursor(
  url: string,
): Promise<{ hits: SearchHit[]; nextCursor: string }> {
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw await parseErrorResponse(res);
  const hits = (await res.json()) as SearchHit[];
  const nextCursor = res.headers.get('X-Next-Cursor') ?? '';
  return { hits, nextCursor };
}

// usePagedSearch powers the /search page. It owns cursor state and
// exposes a `loadMore()` callback so the page can offer "Show more".
// `query` and `filters` reset the cursor automatically.
export function usePagedSearch(
  query: string,
  filters: PagedSearchFilters,
  limit = 30,
) {
  const trimmed = query.trim();
  const [pages, setPages] = useState<{ hits: SearchHit[]; nextCursor: string }[]>([]);
  const [cursor, setCursor] = useState('');
  const [error, setError] = useState<Error | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  // Reset on q / filter change so we don't append onto stale results.
  useEffect(() => {
    setPages([]);
    setCursor('');
    setError(undefined);
  }, [trimmed, filters.format, filters.repo, filters.kind]);

  // Fetch the active page (cursor === current request key).
  useEffect(() => {
    if (!trimmed) return;
    let cancelled = false;
    setIsLoading(true);
    setError(undefined);
    const url = buildSearchURL(trimmed, filters, cursor, limit);
    fetchSearchWithCursor(url)
      .then((page) => {
        if (cancelled) return;
        setPages((prev) => {
          // If cursor is empty, this is page 0 — replace.
          if (!cursor) return [page];
          return [...prev, page];
        });
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimmed, filters.format, filters.repo, filters.kind, cursor, limit]);

  const allHits = pages.flatMap((p) => p.hits);
  const lastCursor = pages.at(-1)?.nextCursor ?? '';

  function loadMore() {
    if (!lastCursor || isLoading) return;
    setCursor(lastCursor);
  }

  function reset() {
    setPages([]);
    setCursor('');
  }

  return {
    hits: allHits,
    nextCursor: lastCursor,
    isLoading,
    error,
    loadMore,
    reset,
    pageCount: pages.length,
  };
}
